import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SendOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Collapse,
  Empty,
  Input,
  Popconfirm,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
  Typography,
} from "antd";
import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PriorityMark, PurchaseRequestStatus } from "../../enum/common";
import { DepartmentCode } from "../../enum/roles";
import {
  IProductionListItem,
  IProductionPurchaseRequest,
} from "../../models/Production";
import {
  useApproveProductionPurchaseRequestByDirector,
  useGetProductionProjectByAlias,
  useGetProductionPurchaseRequest,
  useRequestProductionPurchaseRequestToDirector,
  useUpdateProductionPurchaseRequest,
} from "../../query/ProductionQuery";
import { RootState } from "../../store";
import { notify } from "../../utils/toast";
import ModalViewListAwb from "../modal/ModalViewListAwb";
import DrawerCreateProductionPurchaseRequest from "./DrawerCreateProductionPurchaseRequest";
import DrawerEditProductionPurchaseRequest from "./DrawerEditProductionPurchaseRequest";

const { Text } = Typography;

interface IProductionPurchaseRequestTabProps {
  alias: string;
}

const ProductionPurchaseRequestTab = ({
  alias,
}: IProductionPurchaseRequestTabProps) => {
  const [materialId, setMaterialId] = useState<number>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);
  const [partNumber, setPartNumber] = useState<string>("");
  const [selectedRequest, setSelectedRequest] =
    useState<IProductionPurchaseRequest | null>(null);
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const { data: project } = useGetProductionProjectByAlias(alias!);
  const { data: prdPurchaseRequest, isLoading: prdPurchaseRequestLoading } =
    useGetProductionPurchaseRequest(project?.id!, partNumber);
  const requestPurchaseRequestToDirector =
    useRequestProductionPurchaseRequestToDirector();
  const approvePurchaseRequestByDirector =
    useApproveProductionPurchaseRequestByDirector();
  const updatePurchaseRequest = useUpdateProductionPurchaseRequest();
  const handleSearch = useMemo(
    () => debounce((v: string) => setPartNumber(v), 500),
    [],
  );

  const handleRequestDirector = async (requestId: number) => {
    try {
      await requestPurchaseRequestToDirector.mutateAsync(requestId);
      notify.success("Đã gửi yêu cầu duyệt cho Giám đốc");
    } catch (error) {
      notify.error("Gửi yêu cầu thất bại");
    }
  };

  const handleApprove = async (requestId: number) => {
    try {
      await approvePurchaseRequestByDirector.mutateAsync(requestId);
      await notify.success("Duyệt thành công");
    } catch (error) {
      notify.error("Duyệt thất bại");
    }
  };

  const getPurchaseStatus = (status: PurchaseRequestStatus) => {
    switch (status) {
      case PurchaseRequestStatus.DRAFT:
        return (
          <Tag color="default" icon={<ClockCircleOutlined />}>
            Nháp
          </Tag>
        );
      case PurchaseRequestStatus.SUBMITTED:
        return (
          <Tag color="processing" icon={<ShoppingCartOutlined />}>
            Đã gửi
          </Tag>
        );
      case PurchaseRequestStatus.APPROVED:
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Đã duyệt
          </Tag>
        );
      case PurchaseRequestStatus.REJECTED:
        return (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            Từ chối
          </Tag>
        );
      case PurchaseRequestStatus.CLOSED:
        return (
          <Tag color="default" icon={<CheckCircleOutlined />}>
            Đã đóng
          </Tag>
        );
      default:
        return null;
    }
  };

  const getApprovalStatus = (request: IProductionPurchaseRequest) => {
    if (request.isApprovedByDirector && request.isApprovedByPurchase) {
      return (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Đã duyệt
        </Tag>
      );
    }

    if (request.isApprovedByDirector && !request.isApprovedByPurchase) {
      return (
        <Tag color="processing" icon={<CheckCircleOutlined />}>
          Giám đốc đã duyệt
        </Tag>
      );
    }
    if (request.isRequestedDirector && !request.isApprovedByDirector) {
      return (
        <Tag color="warning" icon={<ClockCircleOutlined />}>
          Chờ BGĐ duyệt
        </Tag>
      );
    }

    return <Tag color="default">Nháp</Tag>;
  };

  const getPriorityRequest = (priority: PriorityMark) => {
    if (priority === PriorityMark.HIGH)
      return <Tag color="red">Mức độ: Rất gấp</Tag>;
    if (priority === PriorityMark.MEDIUM)
      return <Tag color="orange">Mức độ: Gấp</Tag>;
    return <Tag color="green">Mức độ: Thường</Tag>;
  };

  const getActionButtons = (request: IProductionPurchaseRequest) => {
    const buttons = [];
    if (
      !request.isRequestedDirector &&
      !(userInfo?.department?.departmentCode === DepartmentCode.CEO)
    ) {
      buttons.push(
        <Popconfirm
          key="request-director"
          title="Gửi yêu cầu duyệt cho Giám đốc"
          description="Bạn có chắc chắn muốn gửi yêu cầu này?"
          onConfirm={() => handleRequestDirector(request.id)}
          okText="Xác nhận"
          cancelText="Hủy"
        >
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            className="bg-orange-500"
          >
            Gửi yêu cầu BGĐ
          </Button>
        </Popconfirm>,
      );
    }
    if (!request.isApprovedByPurchase) {
      buttons.push(
        <Button
          key="edit"
          type="primary"
          size="small"
          icon={<EditOutlined />}
          className="bg-yellow-500"
          onClick={() => {
            setSelectedRequest(request);
            setEditDrawerOpen(true);
          }}
        >
          Chỉnh sửa
        </Button>,
      );
    }

    return buttons;
  };

  const materialColumns: TableColumnType<IProductionListItem>[] = [
    {
      title: "AWB",
      dataIndex: "awb",
      key: "awb",
      width: 100,
      render: (_, record) => (
        <span
          onClick={() => {
            setMaterialId(record.id);
            setIsModalOpen(true);
          }}
          className="text-blue-600 cursor-pointer"
        >
          Xem
        </span>
      ),
      align: "center",
    },
    {
      title: "Ngày PR",
      dataIndex: "datePr",
      key: "datePr",
      width: 110,
      render: (value: Date) => new Date(value).toLocaleDateString("vi-VN"),
      align: "center",
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 130,
      fixed: "left" as const,
      align: "center",
    },
    {
      title: "Mã nội bộ",
      dataIndex: "internalCode",
      key: "internalCode",
      width: 120,
      align: "center",
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      key: "projectCode",
      width: 120,
      align: "center",
    },
    {
      title: "Danh mục",
      dataIndex: "category",
      key: "category",
      width: 120,
      align: "center",
    },
    {
      title: "Nhà sản xuất",
      dataIndex: "manufacturer",
      key: "manufacturer",
      width: 150,
      ellipsis: true,
      align: "center",
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      align: "center",
    },
    {
      title: "Đơn vị",
      dataIndex: "unit",
      key: "unit",
      width: 80,
      align: "center",
    },
    {
      title: "Số lượng",
      dataIndex: "quantity",
      key: "quantity",
      width: 90,
      align: "center",
    },
    {
      title: "Nguồn",
      dataIndex: "source",
      key: "source",
      width: 120,
      ellipsis: true,
      align: "center",
    },
    {
      title: "CRD",
      dataIndex: "crd",
      key: "crd",
      width: 100,
      align: "center",
    },
    {
      title: "Đơn giá EXW (USD)",
      dataIndex: "unitPriceExwUsd",
      key: "unitPriceExwUsd",
      width: 130,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("en-US"),
    },
    {
      title: "Tổng EXW (USD)",
      dataIndex: "totalPriceExwUsd",
      key: "totalPriceExwUsd",
      width: 130,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("en-US"),
    },
    {
      title: "Đơn giá (VND)",
      dataIndex: "unitPriceVnd",
      key: "unitPriceVnd",
      width: 140,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("vi-VN"),
    },
    {
      title: "Tổng (VND)",
      dataIndex: "totalPriceVnd",
      key: "totalPriceVnd",
      width: 140,
      align: "right",
      render: (value: number) => (
        <strong>{Number(value).toLocaleString("vi-VN")}</strong>
      ),
    },
    {
      title: "Phí ship (VND)",
      dataIndex: "shippingVnd",
      key: "shippingVnd",
      width: 140,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("vi-VN"),
    },
    {
      title: "Thuế NK(%)",
      dataIndex: "importTax",
      key: "importTax",
      width: 140,
      align: "right",
      render: (value: number) => `${Number(value).toLocaleString("en-US")} %`,
    },
    {
      title: "Thuế NK (VNĐ)",
      dataIndex: "totalImportTaxVnd",
      key: "totalImportTaxVnd",
      width: 140,
      align: "right",
      render: (value: number) => `${Number(value).toLocaleString("vi-VN")}`,
    },
    {
      title: "Tổng cộng (VND)",
      dataIndex: "grandTotalVnd",
      key: "grandTotalVnd",
      width: 160,
      align: "right",
      render: (value: number) => (
        <strong className="text-blue-600">
          {Number(value).toLocaleString("vi-VN")}
        </strong>
      ),
    },
    {
      title: "Link",
      dataIndex: "link",
      key: "link",
      width: 80,
      render: (value: string) =>
        value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            <EyeOutlined />
          </a>
        ) : (
          "-"
        ),
      align: "center",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Input
          placeholder="Tìm kiếm yêu cầu mua theo Part Number"
          allowClear
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: "100%" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-green-600"
            onClick={() => setDrawerOpen(true)}
          >
            Tạo yêu cầu mua
          </Button>
        </Space>
      </div>
      <Spin spinning={prdPurchaseRequestLoading}>
        {!prdPurchaseRequest || prdPurchaseRequest.length === 0 ? (
          <Card>
            <Empty
              description="Chưa có đề xuất mua hàng nào"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        ) : (
          <Card>
            <Collapse
              activeKey={expandedKeys}
              onChange={(keys) => setExpandedKeys(keys as string[])}
              items={prdPurchaseRequest.map((request) => ({
                key: request.id.toString(),
                label: (
                  <div className="flex justify-between items-center w-full">
                    <Space>
                      <span className="font-medium">Đề xuất #{request.id}</span>
                      {getApprovalStatus(request)}
                      {getPurchaseStatus(request.status)}
                      {getPriorityRequest(request.priority)}
                    </Space>
                    <Space className="text-sm text-gray-500 mr-2">
                      <span>Người tạo: {request.requestBy}</span>
                      <span>|</span>
                      <span>
                        Ngày tạo:{" "}
                        {new Date(request.requestDate).toLocaleDateString(
                          "vi-VN",
                        )}
                      </span>
                      <span>|</span>
                      <span>Tỉ giá: {request.exchangeRate}</span>
                      <span>|</span>
                      <span className="font-semibold text-blue-600">
                        {Number(request.proposalVnd).toLocaleString("vi-VN")}{" "}
                        VND
                      </span>
                    </Space>
                  </div>
                ),
                extra: (
                  <Space size="small" onClick={(e) => e.stopPropagation()}>
                    {getActionButtons(request)}
                    {userInfo?.department.departmentCode ===
                      DepartmentCode.CEO && (
                      <Popconfirm
                        title="Duyệt đề xuất"
                        description="Bạn có chắc chắn muốn duyệt đề xuất này?"
                        onConfirm={() => handleApprove(request.id)}
                        okText="Xác nhận"
                        cancelText="Hủy"
                      >
                        <Button
                          type="primary"
                          size="small"
                          icon={<CheckOutlined />}
                          loading={requestPurchaseRequestToDirector.isPending}
                          disabled={
                            request.isApprovedByDirector ||
                            !request.isRequestedDirector
                          }
                        >
                          {request.isApprovedByDirector ? "Đã duyệt" : "Duyệt"}
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                ),
                children: (
                  <div className="space-y-3">
                    {request.note && (
                      <div className="p-3 rounded">
                        <Text type="secondary" className="text-xs">
                          Ghi chú:
                        </Text>
                        <div className="text-sm text-red-500 mt-1">
                          {request.note}
                        </div>
                      </div>
                    )}
                    <Table
                      columns={materialColumns}
                      dataSource={request.prdListItems}
                      rowKey={(record) => record.id}
                      pagination={false}
                      scroll={{ x: 2000 }}
                      size="small"
                      summary={(pageData) => {
                        const totalVnd = pageData.reduce(
                          (sum, item) => sum + Number(item.grandTotalVnd),
                          0,
                        );
                        return (
                          <Table.Summary.Row className="font-semibold ">
                            <Table.Summary.Cell
                              index={1}
                              colSpan={18}
                              align="right"
                            >
                              <Text strong>Tổng cộng:</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              <Text strong className="text-blue-600">
                                {totalVnd.toLocaleString("vi-VN")} VND
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} />
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  </div>
                ),
              }))}
            />
          </Card>
        )}
      </Spin>

      {materialId !== undefined && (
        <ModalViewListAwb
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          materialId={materialId}
          type="production"
        />
      )}

      {project && (
        <>
          <DrawerCreateProductionPurchaseRequest
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            projectId={project.id}
          />
          <DrawerEditProductionPurchaseRequest
            open={editDrawerOpen}
            onClose={() => {
              setEditDrawerOpen(false);
              setSelectedRequest(null);
            }}
            purchaseRequest={selectedRequest}
            onSubmit={async (id, data) => {
              try {
                await updatePurchaseRequest.mutateAsync({ id, data });
                notify.success("Cập nhật đề xuất thành công");
                setEditDrawerOpen(false);
                setSelectedRequest(null);
              } catch (error) {
                notify.error("Có lỗi xảy ra");
              }
            }}
            loading={updatePurchaseRequest.isPending}
          />
        </>
      )}
    </div>
  );
};

export default ProductionPurchaseRequestTab;
